"use client";

import Link from "next/link";
import { useTranslation } from "@/lib/i18n/context";
import { RealityDemo } from "./RealityDemo";

const COPY = {
  kicker: { en: "The Reality", sl: "Resničnost" },
  titleA: { en: "The cost of ", sl: "Cena " },
  titleEm: { en: "fragmented days.", sl: "razdrobljenih dni." },
  prose: {
    en: "Your tasks live in one tool. Your calendar in another. And your long-term goals? They are quietly waiting for a free moment that never comes. You end your days exhausted, checking off endless minor items, while wondering if you actually made any progress on what truly matters.",
    sl: "Tvoja opravila živijo v enem orodju. Koledar v drugem. Tvoji dolgoročni cilji pa? Tiho čakajo na prost trenutek, ki nikoli ne pride. Dneve končaš izčrpan, odkljukaš neskončno drobnih stvari in se sprašuješ, ali si sploh napredoval pri tistem, kar resnično šteje.",
  },
  proseLead: {
    en: "Journail changes how your day is built.",
    sl: "Journail spremeni, kako je zgrajen tvoj dan.",
  },
  proseRest: {
    en: " By placing your high-level vision directly into your daily workspace, it ensures that your big goals are never pushed aside by urgent noise.",
    sl: " S tem ko tvojo vizijo na visoki ravni postavi naravnost v tvoj delovni prostor, poskrbi, da velikih ciljev nikoli ne odrine nujni hrup.",
  },
  sublead: {
    en: "Journail reads your goals, your open tasks, and your calendars — then builds one clear plan for today. No rigid clock; you work at your own rhythm.",
    sl: "Journail prebere tvoje cilje, odprta opravila in koledarje — nato sestavi en jasen načrt za danes. Brez toge ure; delaš v svojem ritmu.",
  },
  more: { en: "Explore goals", sl: "Spoznaj cilje" },
} as const;

export function RealitySection() {
  const { lang } = useTranslation();
  const en = lang === "en";

  return (
    <section className="reality">
      <div className="section">
        <div className="reality-head">
          <div className="r-kicker">
            <span className="kdot" />
            {en ? COPY.kicker.en : COPY.kicker.sl}
          </div>
          <h2 className="r-title">
            {en ? COPY.titleA.en : COPY.titleA.sl}
            <em>{en ? COPY.titleEm.en : COPY.titleEm.sl}</em>
          </h2>
          <p className="r-prose">{en ? COPY.prose.en : COPY.prose.sl}</p>
          <p className="r-prose-em">
            <span className="lead-line">
              {en ? COPY.proseLead.en : COPY.proseLead.sl}
            </span>
            {en ? COPY.proseRest.en : COPY.proseRest.sl}
          </p>
        </div>

        <div className="subhead">
          <p className="sublead">{en ? COPY.sublead.en : COPY.sublead.sl}</p>
        </div>

        <RealityDemo lang={lang} />

        <div className="mt-6 lg:mt-10 flex justify-center">
          <Link
            href="/product/goals"
            className="inline-flex items-center font-mono text-[12px] tracking-[0.16em] uppercase text-[var(--terracotta)] underline-offset-4 hover:underline"
          >
            {`${en ? COPY.more.en : COPY.more.sl} →`}
          </Link>
        </div>
      </div>

      <style jsx global>{`
        .reality {
          position: relative;
        }

        /* ── Section / centering ── */
        .reality .section {
          position: relative;
          width: 100%;
          max-width: 460px;
          margin: 0 auto;
          padding: 0;
        }

        /* ── Header ── */
        .reality .reality-head {
          text-align: center;
          padding: 48px 24px 0;
        }
        .reality .r-kicker {
          font-family: var(--f-mono, "Geist Mono", monospace);
          font-size: 10.5px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--ink-soft);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          margin-bottom: 18px;
        }
        .reality .r-kicker .kdot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--terracotta);
          box-shadow: 0 0 0 3px var(--terra-soft);
        }
        .reality .r-title {
          font-family: var(--f-display, "Fraunces", serif);
          font-weight: 350;
          font-size: 31px;
          line-height: 1.05;
          letter-spacing: -0.022em;
          margin: 0 0 18px;
          font-variation-settings: "opsz" 120, "SOFT" 30;
        }
        .reality .r-title em {
          font-style: italic;
          color: var(--terracotta);
          font-variation-settings: "opsz" 120, "SOFT" 100;
        }
        .reality .r-prose {
          font-size: 15px;
          line-height: 1.55;
          color: var(--ink-soft);
          margin: 0 0 20px;
          text-wrap: pretty;
        }
        .reality .r-prose-em {
          font-family: var(--f-display);
          font-style: italic;
          font-weight: 400;
          font-size: 18px;
          line-height: 1.42;
          color: var(--ink);
          margin: 0;
          font-variation-settings: "opsz" 72, "SOFT" 60;
          text-wrap: pretty;
        }
        .reality .r-prose-em .lead-line {
          color: var(--terracotta);
        }

        /* ── Subhead ── */
        .reality .subhead {
          text-align: center;
          padding: 22px 24px 26px;
        }
        .reality .sublead {
          font-size: 15px;
          line-height: 1.55;
          color: var(--ink-soft);
          margin: 0;
          text-wrap: pretty;
        }

        /* ── Stage ── */
        .reality .stage {
          position: relative;
          padding: 6px 24px 40px;
        }
        .reality .sources {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 9px;
        }
        .reality .col {
          position: relative;
        }
        .reality .col-head {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
          flex-wrap: wrap;
        }
        .reality .col-dot {
          width: 8px;
          height: 8px;
          border-radius: 2px;
          flex: none;
        }
        .reality .col-name {
          font-family: var(--f-mono);
          font-size: 10px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .reality .col-sub {
          font-size: 9.5px;
          line-height: 1.2;
          color: var(--ink-faint);
          flex-basis: 100%;
          margin: -2px 0 7px 14px;
          min-height: 12px;
        }
        .reality .col.goal .col-dot {
          background: var(--terracotta);
        }
        .reality .col.goal .col-name {
          color: var(--terracotta);
        }
        .reality .col.task .col-dot {
          background: var(--moss);
        }
        .reality .col.task .col-name {
          color: var(--moss);
        }
        .reality .col.cal .col-dot {
          background: var(--ink-soft);
        }
        .reality .col.cal .col-name {
          color: var(--ink-soft);
        }
        .reality .chips {
          display: flex;
          flex-direction: column;
        }
        .reality .chip {
          position: relative;
          background: var(--paper);
          border: 1px solid var(--hair);
          border-radius: 9px;
          padding: 8px 9px;
          margin-bottom: 7px;
          box-shadow: 0 2px 6px rgba(26, 24, 21, 0.05);
          transition: opacity 0.35s ease,
            transform 0.35s cubic-bezier(0.2, 0.7, 0.2, 1),
            box-shadow 0.3s ease;
        }
        .reality .chip .ct {
          font-size: 11.5px;
          line-height: 1.2;
          color: var(--ink);
          font-weight: 450;
        }
        .reality .chip .cmeta {
          font-family: var(--f-mono);
          font-size: 8.5px;
          color: var(--ink-faint);
          margin-top: 3px;
          letter-spacing: 0.02em;
        }
        .reality .chip .cmeta.carry {
          color: var(--moss);
        }
        .reality .chip.lit {
          box-shadow: 0 4px 14px rgba(184, 83, 26, 0.16);
          transform: translateY(-1px);
        }
        .reality .col.goal .chip.lit {
          box-shadow: 0 4px 14px rgba(184, 83, 26, 0.2);
          border-color: #b8531a55;
        }
        .reality .col.task .chip.lit {
          box-shadow: 0 4px 14px rgba(42, 74, 59, 0.2);
          border-color: #2a4a3b55;
        }
        .reality .col.cal .chip.lit {
          box-shadow: 0 4px 14px rgba(26, 24, 21, 0.16);
          border-color: #1a181544;
        }
        .reality .chip.spent {
          opacity: 0.3;
          transform: scale(0.97);
          box-shadow: none;
        }

        /* ── Funnel ── */
        .reality .funnel {
          position: relative;
          height: 58px;
          margin: 2px 0 -4px;
        }
        .reality .funnel svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          display: block;
        }
        .reality .fn-line {
          fill: none;
          stroke: var(--rule);
          stroke-width: 1;
          stroke-dasharray: 2 4;
          vector-effect: non-scaling-stroke;
        }
        .reality .funnel-mobile {
          display: block;
        }
        .reality .funnel-desktop {
          display: none;
        }

        /* ── Flier ── */
        .reality .flier {
          position: absolute;
          top: 0;
          left: 0;
          width: 13px;
          height: 13px;
          border-radius: 4px;
          z-index: 6;
          opacity: 0;
          pointer-events: none;
          will-change: transform, opacity;
          margin: -6.5px 0 0 -6.5px;
          box-shadow: 0 3px 8px rgba(26, 24, 21, 0.25);
          transform: translate(var(--x0, 0), var(--y0, 0));
        }
        .reality .flier.goal {
          background: var(--terracotta);
        }
        .reality .flier.task {
          background: var(--moss);
        }
        .reality .flier.cal {
          background: var(--ink-soft);
        }
        .reality .flier.go {
          animation: realityFlyDot 660ms cubic-bezier(0.5, 0, 0.2, 1) forwards;
        }
        @keyframes realityFlyDot {
          0% {
            transform: translate(var(--x0), var(--y0)) scale(1);
            opacity: 1;
          }
          14% {
            opacity: 1;
          }
          80% {
            opacity: 1;
          }
          100% {
            transform: translate(var(--x1), var(--y1)) scale(0.5);
            opacity: 0;
          }
        }

        /* ── Plan ── */
        .reality .plan {
          position: relative;
          background: var(--paper);
          border: 1px solid var(--hair);
          border-radius: 14px;
          padding: 15px 16px 14px;
          box-shadow: 0 12px 32px rgba(26, 24, 21, 0.09);
          animation: realityFadeUp 0.6s 0.18s both;
        }
        @keyframes realityFadeUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: none;
          }
        }
        .reality .plan-cap {
          font-family: var(--f-mono);
          font-size: 9px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--ink-faint);
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
        }
        .reality .plan-cap .ai {
          color: var(--terracotta);
          display: inline-flex;
          align-items: center;
          gap: 5px;
          opacity: 0;
          transition: opacity 0.4s ease;
        }
        .reality .plan-cap .ai.show {
          opacity: 1;
        }
        .reality .plan-cap .ai .pulse {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--terracotta);
          box-shadow: 0 0 0 3px var(--terra-soft);
        }
        .reality .plan-title {
          font-family: var(--f-display);
          font-size: 18px;
          font-weight: 430;
          margin: 0 0 8px;
        }
        .reality .plan-list {
          position: relative;
          min-height: 226px;
        }
        .reality .plan-empty {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: var(--ink-faint);
          font-size: 12.5px;
          line-height: 1.5;
          padding: 0 18px;
          transition: opacity 0.4s ease;
        }
        .reality .prow {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 0;
          border-top: 1px solid var(--hair);
          font-size: 13.5px;
        }
        .reality .prow:first-child {
          border-top: 0;
        }
        .reality .prow.in {
          animation: realityRowIn 0.42s cubic-bezier(0.2, 0.7, 0.2, 1) backwards;
        }
        @keyframes realityRowIn {
          from {
            opacity: 0;
            transform: translateY(7px);
          }
          to {
            opacity: 1;
            transform: none;
          }
        }
        .reality .prow .nm {
          flex: 1;
          color: var(--ink);
        }
        .reality .prow .nm small {
          display: block;
          font-family: var(--f-mono);
          font-size: 8.5px;
          color: var(--moss);
          letter-spacing: 0.03em;
          margin-top: 1px;
        }
        .reality .prow .src {
          font-family: var(--f-mono);
          font-size: 8px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          padding: 2.5px 5px;
          border-radius: 4px;
          white-space: nowrap;
          flex: none;
        }
        .reality .prow .src.goal {
          color: var(--terracotta);
          background: var(--terra-soft);
        }
        .reality .prow .src.task {
          color: var(--moss);
          background: var(--moss-soft);
        }
        .reality .prow .src.cal {
          color: var(--ink-soft);
          background: var(--cal-soft);
        }
        .reality .plan-foot {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 11px;
          padding-top: 11px;
          border-top: 1px solid var(--hair);
          font-size: 11.5px;
          color: var(--ink-soft);
          opacity: 0;
          transition: opacity 0.45s ease;
        }
        .reality .plan-foot.show {
          opacity: 1;
        }
        .reality .plan-foot .tick {
          width: 16px;
          height: 16px;
          border-radius: 5px;
          background: var(--terracotta);
          flex: none;
          position: relative;
        }
        .reality .plan-foot .tick::after {
          content: "";
          position: absolute;
          left: 5px;
          top: 3px;
          width: 4px;
          height: 7.5px;
          border: solid #fff;
          border-width: 0 1.7px 1.7px 0;
          transform: rotate(40deg);
        }

        /* ── Replay ── */
        .reality .replay {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          margin: 18px 0 0;
          font-family: var(--f-mono);
          font-size: 10px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--ink-faint);
          background: none;
          border: 0;
          cursor: pointer;
          padding: 6px 0;
        }
        .reality .replay svg {
          transition: transform 0.4s ease;
        }
        .reality .replay:hover svg {
          transform: rotate(-90deg);
        }

        .reality .dropmark {
          position: absolute;
          top: 4px;
          left: 50%;
          width: 1px;
          height: 1px;
          pointer-events: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .reality .prow.in {
            animation: none;
          }
          .reality .chip {
            transition: none;
          }
        }

        /* ═══════════════ Desktop (≥1024px) ═══════════════ */
        @media (min-width: 1024px) {
          .reality .section {
            position: relative;
            width: 100%;
            max-width: 1180px;
            margin: 0 auto;
            padding: 72px 56px 80px;
          }
          .reality .reality-head {
            text-align: center;
            max-width: 940px;
            margin: 0 auto 8px;
            padding: 0;
          }
          .reality .r-kicker {
            font-size: 13px;
            letter-spacing: 0.22em;
            gap: 12px;
            margin-bottom: 28px;
          }
          .reality .r-kicker .kdot {
            width: 7px;
            height: 7px;
            box-shadow: 0 0 0 4px var(--terra-soft);
          }
          .reality .r-title {
            font-size: 60px;
            line-height: 1.04;
            margin: 0 0 30px;
            font-variation-settings: "opsz" 144, "SOFT" 30;
          }
          .reality .r-title em {
            font-variation-settings: "opsz" 144, "SOFT" 100;
          }
          .reality .r-prose {
            font-size: 20px;
            max-width: 62ch;
            margin: 0 auto 36px;
          }
          .reality .r-prose-em {
            font-size: 25px;
            max-width: 44ch;
            margin: 0 auto;
          }
          .reality .subhead {
            text-align: center;
            max-width: 760px;
            margin: 36px auto 44px;
            padding: 0;
          }
          .reality .sublead {
            font-size: 20px;
            max-width: 62ch;
            margin: 0 auto;
          }
          .reality .stage {
            display: grid;
            grid-template-columns: 1fr 132px 1fr;
            align-items: stretch;
            gap: 0;
            padding: 0;
          }
          .reality .sources {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }
          .reality .col {
            background: color-mix(in oklab, var(--paper) 50%, transparent);
            border: 1px solid var(--hair);
            border-radius: 14px;
            padding: 16px 18px;
            flex: 1;
            display: flex;
            flex-direction: column;
          }
          .reality .col-head {
            gap: 9px;
            margin-bottom: 0;
            flex-wrap: nowrap;
          }
          .reality .col-dot {
            width: 10px;
            height: 10px;
            border-radius: 3px;
          }
          .reality .col-name {
            font-size: 12px;
            letter-spacing: 0.1em;
          }
          .reality .col-sub {
            font-size: 11.5px;
            color: var(--ink-faint);
            margin: 0 0 0 auto;
            flex-basis: auto;
            min-height: 0;
          }
          .reality .chips {
            display: flex;
            flex-direction: row;
            gap: 10px;
            margin-top: 13px;
          }
          .reality .chip {
            flex: 1;
            border-radius: 10px;
            padding: 11px 13px;
            margin-bottom: 0;
            box-shadow: 0 2px 8px rgba(26, 24, 21, 0.05);
          }
          .reality .chip .ct {
            font-size: 13.5px;
          }
          .reality .chip .cmeta {
            font-size: 9.5px;
            margin-top: 4px;
          }
          .reality .chip.lit {
            box-shadow: 0 6px 18px rgba(184, 83, 26, 0.16);
            transform: translateY(-2px);
          }
          .reality .col.goal .chip.lit {
            box-shadow: 0 6px 18px rgba(184, 83, 26, 0.22);
            border-color: #b8531a55;
          }
          .reality .col.task .chip.lit {
            box-shadow: 0 6px 18px rgba(42, 74, 59, 0.22);
            border-color: #2a4a3b55;
          }
          .reality .col.cal .chip.lit {
            box-shadow: 0 6px 18px rgba(26, 24, 21, 0.16);
            border-color: #1a181544;
          }
          .reality .chip.spent {
            opacity: 0.32;
            transform: scale(0.975);
          }
          .reality .funnel {
            height: auto;
            margin: 0;
          }
          .reality .funnel-mobile {
            display: none;
          }
          .reality .funnel-desktop {
            display: block;
          }
          .reality .flier {
            width: 15px;
            height: 15px;
            margin: -7.5px 0 0 -7.5px;
            box-shadow: 0 3px 10px rgba(26, 24, 21, 0.25);
          }
          .reality .flier.go {
            animation: realityFlyDot 680ms cubic-bezier(0.5, 0, 0.2, 1) forwards;
          }
          .reality .plan {
            border-radius: 18px;
            padding: 22px 24px 20px;
            box-shadow: 0 18px 44px rgba(26, 24, 21, 0.1);
            display: flex;
            flex-direction: column;
            animation: none;
          }
          .reality .dropmark {
            top: 50%;
            left: 4px;
          }
          .reality .plan-cap {
            font-size: 10.5px;
            margin-bottom: 6px;
          }
          .reality .plan-cap .ai {
            gap: 6px;
          }
          .reality .plan-cap .ai .pulse {
            width: 6px;
            height: 6px;
          }
          .reality .plan-title {
            font-size: 24px;
            margin: 0 0 6px;
          }
          .reality .plan-list {
            flex: 1;
            min-height: 306px;
          }
          .reality .plan-empty {
            font-size: 14px;
            padding: 0 24px;
          }
          .reality .prow {
            gap: 14px;
            padding: 13px 2px;
            font-size: 15.5px;
          }
          .reality .prow .nm small {
            font-size: 9.5px;
            margin-top: 2px;
          }
          .reality .prow .src {
            font-size: 9px;
            padding: 3px 7px;
            border-radius: 5px;
          }
          .reality .plan-foot {
            gap: 10px;
            margin-top: 14px;
            padding-top: 14px;
            font-size: 13px;
          }
          .reality .plan-foot .tick {
            width: 18px;
            height: 18px;
            border-radius: 6px;
          }
          .reality .plan-foot .tick::after {
            left: 6px;
            top: 3.5px;
            width: 4.5px;
            height: 8.5px;
            border-width: 0 1.8px 1.8px 0;
          }
          .reality .replay {
            position: absolute;
            right: 56px;
            top: 78px;
            margin: 0;
            border: 1px solid var(--rule);
            border-radius: 8px;
            padding: 8px 12px;
            font-size: 11px;
            transition: color 0.2s, border-color 0.2s;
          }
          .reality .replay:hover {
            color: var(--terracotta);
            border-color: #b8531a55;
          }
        }
      `}</style>
    </section>
  );
}
